import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faMinus, faPlus, faStar } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import Button from '../../common/Button';
import { ProductData, ProductModalProps, Size, Ingredient } from '../../../types/homepage';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import Spinner from '../../common/Spinner';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { NotificationType, Position } from '../../../types/enums/notificationEnums';
import { Link } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency, formatPercentage } from '../../../i18n/FormatCurrency';
import { alanyakebabLogo } from '../../../assets/images';
import { getImageSrc } from '../../../utils/imageFallback';

const ProductModal: React.FC<ProductModalProps> = ({
  modalOpen,
  setModalOpen,
  productID,
  categories
}) => {
  const { t } = useTranslation();
  const [size, setSize] = useState<Size | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedIngredients, setSelectedIngredients] = useState<number[]>([]);
  const { user, isAuthenticated } = useAuth();
  const userID = user?.id;
  const { addToCart, loading } = useCart();
  const { showNotification } = useNotification();

  //Fetch product data when modal opens
  useEffect(() => {
    const fetchProductById = async () => {
      if (!modalOpen || !productID) return;

      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get(`/products/get-product/${productID}`);
        if (response.data.data) {
          const product = response.data.data as ProductData;
          setProductData(product);
          if (product.sizes && product.sizes.length > 0) {
            setSize(product.sizes[0]);
          }
          if (product.ingredients && product.ingredients.length > 0) {
            setSelectedIngredients(product.ingredients.map((ing: Ingredient) => ing.ingredient_id));
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        setError('Failed to load product data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductById();
  }, [productID, modalOpen]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setModalOpen(false);
      }
    };

    if (modalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalOpen, setModalOpen]);

  const handleQuantityChange = (increment: number) => {
    setQuantity(prevQuantity => Math.max(1, prevQuantity + increment));
  };

  const toggleIngredient = (ingredientId: number) => {
    setSelectedIngredients(prev =>
      prev.includes(ingredientId) ? prev.filter(id => id !== ingredientId) : [...prev, ingredientId]
    );
  };

  const getEffectiveDiscount = (product: any) => {
    const productDiscount = parseFloat(product.discount_percent);
    const category = categories.find(c => c.id.toString() === product.category_id.toString());
    const categoryDiscount = category ? parseFloat(category.discount_percent) : 0;
    return Math.max(productDiscount, categoryDiscount);
  };

  const calculateDiscountedPrice = (price: string, discountPercent: number) => {
    const basePrice = parseFloat(price);
    return basePrice * (1 - discountPercent / 100) * quantity;
  };

  if (!modalOpen) return null;

  const handleAddToCart = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!productData || !size) {
      showNotification(
        'Unable to add product to cart. Please try again.',
        NotificationType.Error,
        Position.TopRight
      );
      return;
    }

    const note = (event.currentTarget.productNote as HTMLTextAreaElement).value.trim();

    // Check if custom product has ingredients specified
    if (Boolean(productData.is_custom) && !note) {
      showNotification(
        t('For custom dishes, you must specify your desired ingredients.'),
        NotificationType.Warning,
        Position.TopRight
      );
      return;
    }

    // Preparing the data to send to the API
    const cartItemData = {
      size_id: size ? size.size_id : null,
      quantity: quantity,
      item_note: note
    };

    const cartItemIngredientsData = {
      ingredients: selectedIngredients.map(id => ({
        ingredient_id: id,
        is_included: true
      }))
    };

    const requestData = {
      userId: userID,
      cartItemData,
      cartItemIngredientsData
    };

    setModalOpen(false);
    await addToCart(requestData);
  };

  return (
    <AnimatePresence>
      {modalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white dark:bg-stone-800 w-[350px] md:w-[450px] max-h-[90vh] rounded-3xl shadow-xl"
          >
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Spinner />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center h-64">
                <p className="text-red-500">{error}</p>
              </div>
            ) : productData ? (
              <form onSubmit={handleAddToCart}>
                <div className="bg-orange-400 max-w-[450px] h-auto flex justify-center rounded-3xl p-2  relative shadow-lg shadow-gray-500/20 dark:shadow-orange-900 dark:shadow-md">
                  <button
                    className="absolute top-4 right-4 cursor-pointer p-1 rounded-full flex justify-center items-center"
                    onClick={() => setModalOpen(false)}
                  >
                    <FontAwesomeIcon icon={faXmark} className="text-white text-2xl" />
                  </button>
                  <img
                    src={getImageSrc(productData.image_url, alanyakebabLogo)}
                    alt={productData.product_name}
                    className="h-32 md:h-40 lg:h-48  object-cover"
                    onError={e => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = alanyakebabLogo;
                    }}
                  />
                </div>
                <div className="h-80 overflow-y-auto">
                  <div className="px-6 py-2 mt-4 flex flex-col gap-6">
                    <div>
                      <h3 className="text-2xl md:text-3xl font-semibold text-black dark:text-white break-words flex items-center">
                        {productData.product_name}
                        {Boolean(productData.is_custom) && (
                          <span className="ml-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full inline-flex items-center">
                            <FontAwesomeIcon icon={faStar} className="text-white mr-1" />
                            {t('Custom')}
                          </span>
                        )}
                      </h3>
                      <p className="text-md md:text-lg text-stone-400 dark:text-stone-300 mt-2">
                        {productData.description}
                      </p>
                      {Boolean(productData.is_custom) && (
                        <p className="text-amber-600 dark:text-amber-400 mt-2 text-sm font-medium">
                          {t(
                            'This is a custom dish. Please specify your desired ingredients in the notes below.'
                          )}
                        </p>
                      )}
                    </div>
                    {productData.sizes && productData.sizes.length > 0 && (
                      <div className="flex gap-4 items-center flex-wrap">
                        <p className="text-lg md:text-xl font-medium text-stone-400 dark:text-stone-300">
                          {t('Size')}:
                        </p>
                        {productData.sizes.map(sizeOption => (
                          <button
                            key={sizeOption.size_name}
                            className={clsx(
                              'text-xs md:text-md px-4 py-2 rounded-3xl',
                              size?.size_name === sizeOption.size_name
                                ? 'bg-orange-500 text-white font-medium'
                                : 'bg-zinc-200 dark:bg-zinc-100'
                            )}
                            onClick={() => setSize(sizeOption)}
                            type="button"
                          >
                            {sizeOption.size_name}
                          </button>
                        ))}
                      </div>
                    )}
                    {productData.ingredients && productData.ingredients.length > 0 && (
                      <div>
                        <p className="text-lg md:text-xl font-medium text-black dark:text-white mb-2">
                          {t('Ingredients')}
                        </p>
                        <div className="flex gap-4 flex-wrap">
                          {productData.ingredients.map(ingredient => (
                            <button
                              key={ingredient.ingredient_id}
                              className={clsx(
                                'text-xs md:text-md px-4 py-2 rounded-3xl',
                                selectedIngredients.includes(ingredient.ingredient_id)
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-zinc-200 dark:bg-zinc-100'
                              )}
                              onClick={() => toggleIngredient(ingredient.ingredient_id)}
                              type="button"
                            >
                              {ingredient.ingredient_name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col gap-3">
                      <label
                        htmlFor="productNote"
                        className={`text-lg md:text-xl font-medium ${
                          Boolean(productData.is_custom)
                            ? 'text-black dark:text-white flex items-center'
                            : 'text-black dark:text-white'
                        }`}
                      >
                        {Boolean(productData.is_custom) ? (
                          <>
                            {t('Custom Ingredients')}
                            <span className="text-red-500 ml-1">*</span>
                          </>
                        ) : (
                          t('Product Note')
                        )}
                        :
                      </label>
                      <textarea
                        id="productNote"
                        name="productNote"
                        required={Boolean(productData.is_custom)}
                        placeholder={
                          Boolean(productData.is_custom)
                            ? t(
                                'Please list all ingredients you want in your custom dish. This is required for custom orders.'
                              )
                            : t('You can put your notes for the product here.')
                        }
                        className={`px-4 py-2 rounded-xl border ${
                          Boolean(productData.is_custom)
                            ? 'border-amber-300 dark:border-amber-700 bg-white dark:bg-stone-700'
                            : 'border-stone-200 dark:bg-stone-700 dark:border-stone-700'
                        } h-24 resize-none dark:text-zinc-200 dark:placeholder:text-stone-300 placeholder:text-sm md:placeholder:text-md`}
                      />
                      {Boolean(productData.is_custom) && (
                        <p className="text-amber-600 dark:text-amber-400 text-sm">
                          {t(
                            'For custom dishes, please specify your desired ingredients in detail.'
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="rounded-3xl bg-slate-100 dark:bg-stone-700 py-1.5 px-6 md:px-6 md:py-3 shadow-lg shadow-gray-500/10">
                  <div className="flex flex-row justify-between items-center">
                    <div className="flex items-center">
                      {getEffectiveDiscount(productData) > 0 ? (
                        <>
                          <p className="text-gray-500 dark:text-gray-400 line-through text-lg mr-2">
                            {formatCurrency({
                              amount:
                                Number(size?.size_price) * quantity ||
                                Number(productData.base_price) * quantity
                            })}
                            €
                          </p>
                          <p className="text-black text-2xl font-medium dark:text-white mr-2">
                            {formatCurrency({
                              amount: calculateDiscountedPrice(
                                size?.size_price || productData.base_price,
                                getEffectiveDiscount(productData)
                              )
                            })}
                            €
                          </p>
                        </>
                      ) : (
                        <p className="text-black text-2xl font-medium dark:text-white mr-2">
                          {formatCurrency({
                            amount:
                              Number(size?.size_price) * quantity ||
                              Number(productData.base_price) * quantity
                          })}
                          €
                        </p>
                      )}
                      {getEffectiveDiscount(productData) > 0 && (
                        <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {formatPercentage({ value: String(getEffectiveDiscount(productData)) })}
                        </div>
                      )}
                    </div>
                    <div className="bg-stone-900 px-4 py-2 rounded-[3rem] flex gap-3 items-center">
                      <button
                        type="button"
                        className="bg-zinc-600 dark:bg-slate-200 rounded-[5rem] px-1.5 py-0.5"
                        onClick={() => handleQuantityChange(-1)}
                      >
                        <span className="text-white dark:text-black">
                          <FontAwesomeIcon icon={faMinus} />
                        </span>
                      </button>
                      <p className="text-white font-semibold">{quantity}</p>
                      <button
                        type="button"
                        className="bg-zinc-600 dark:bg-slate-200 rounded-[5rem] px-1.5 py-0.5"
                        onClick={() => handleQuantityChange(1)}
                      >
                        <span className="text-white dark:text-black">
                          <FontAwesomeIcon icon={faPlus} />
                        </span>
                      </button>
                    </div>
                  </div>
                  {isAuthenticated ? (
                    <Button
                      variant="tertiary"
                      className="w-full mt-4 h-12 mb-3"
                      type="submit"
                      disabled={loading}
                    >
                      <span className="text-lg font-medium">
                        {loading
                          ? t('Adding to cart...')
                          : Boolean(productData.is_custom)
                            ? t('Add to Cart')
                            : t('Add to Cart')}
                      </span>
                    </Button>
                  ) : (
                    <Link to="/login">
                      <Button variant="tertiary" className="w-full mt-4 h-12 mb-3" type="submit">
                        <span className="text-lg font-medium">{t('Login to order')}</span>
                      </Button>
                    </Link>
                  )}
                </div>
              </form>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;
